import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Download, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getUserWeeklySummary } from '@/utils/api';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, HeadingLevel, WidthType, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { BUSINESS_NAME } from '@/branding';

/**
 * MealLogSummary - Weekly meal summary with PDF/Word export
 * Extracted from AdminDashboard.js for better code organization
 */
const MealLogSummary = ({ userId, token, userEmail }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().split('T')[0];
  });

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUserWeeklySummary(userId, startDate, token);
      setSummaryData(data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load meal summary');
    } finally {
      setLoading(false);
    }
  }, [userId, startDate, token]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getMealTypeLabel = (type) => {
    const labels = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack', drink: 'Drink', late_night: 'Late Night' };
    return labels[type] || type;
  };

  const downloadPDF = () => {
    if (!summaryData) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(107, 142, 35);
    doc.text(`${BUSINESS_NAME} - Meal Log Summary`, pageWidth / 2, 20, { align: 'center' });
    
    // User info
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`User: ${summaryData.user_email}`, 14, 35);
    doc.text(`Period: ${summaryData.start_date} to ${summaryData.end_date}`, 14, 42);
    doc.text(`Days with data: ${summaryData.num_days_with_data}`, 14, 49);
    
    // Meals table
    const tableData = summaryData.meals.map(meal => [
      formatDate(meal.date),
      getMealTypeLabel(meal.meal_type),
      meal.description?.substring(0, 30) + (meal.description?.length > 30 ? '...' : ''),
      meal.calories.toFixed(0),
      meal.protein_g.toFixed(1),
      meal.carbs_g.toFixed(1),
      meal.fat_g.toFixed(1)
    ]);

    autoTable(doc, {
      startY: 55,
      head: [['Date', 'Type', 'Description', 'Cal', 'Protein(g)', 'Carbs(g)', 'Fat(g)']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [107, 142, 35] },
      styles: { fontSize: 8 }
    });

    // Daily averages
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setTextColor(107, 142, 35);
    doc.text('Daily Averages', 14, finalY);
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const avg = summaryData.daily_averages;
    
    // Macros
    doc.setFontSize(11);
    doc.setTextColor(107, 142, 35);
    doc.text('Macronutrients:', 14, finalY + 10);
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Calories: ${avg.calories.toFixed(0)} kcal    Protein: ${avg.protein_g.toFixed(1)}g    Carbs: ${avg.carbs_g.toFixed(1)}g    Fat: ${avg.fat_g.toFixed(1)}g`, 14, finalY + 17);
    
    // Vitamins
    doc.setFontSize(11);
    doc.setTextColor(107, 142, 35);
    doc.text('Vitamins:', 14, finalY + 27);
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Vitamin A: ${(avg.vitamin_a_mcg || 0).toFixed(1)} mcg    Vitamin C: ${(avg.vitamin_c_mg || 0).toFixed(1)} mg    Folate: ${(avg.folate_mcg || 0).toFixed(1)} mcg    B12: ${(avg.vitamin_b12_mcg || 0).toFixed(1)} mcg`, 14, finalY + 34);
    
    // Minerals
    doc.setFontSize(11);
    doc.setTextColor(107, 142, 35);
    doc.text('Minerals:', 14, finalY + 44);
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Calcium: ${(avg.calcium_mg || 0).toFixed(1)} mg    Iron: ${(avg.iron_mg || 0).toFixed(1)} mg    Iodine: ${(avg.iodine_mcg || 0).toFixed(1)} mcg`, 14, finalY + 51);
    doc.text(`Magnesium: ${(avg.magnesium_mg || 0).toFixed(1)} mg    Potassium: ${(avg.potassium_mg || 0).toFixed(1)} mg    Sodium: ${(avg.sodium_mg || 0).toFixed(1)} mg`, 14, finalY + 58);

    // Save
    doc.save(`meal-summary-${summaryData.user_email}-${startDate}.pdf`);
    toast.success('PDF downloaded successfully');
  };

  const downloadWord = async () => {
    if (!summaryData) return;

    const tableRows = [
      new TableRow({
        children: ['Date', 'Type', 'Description', 'Calories', 'Protein(g)', 'Carbs(g)', 'Fat(g)'].map(text => 
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })],
            shading: { fill: '6B8E23' }
          })
        )
      }),
      ...summaryData.meals.map(meal => 
        new TableRow({
          children: [
            formatDate(meal.date),
            getMealTypeLabel(meal.meal_type),
            meal.description?.substring(0, 40) || '',
            meal.calories.toFixed(0),
            meal.protein_g.toFixed(1),
            meal.carbs_g.toFixed(1),
            meal.fat_g.toFixed(1)
          ].map(text => 
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: String(text) })] })]
            })
          )
        })
      )
    ];

    const avg = summaryData.daily_averages;

    const doc = new Document({
      sections: [{
        children: [
          new Paragraph({
            text: `${BUSINESS_NAME} - Meal Log Summary`,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER
          }),
          new Paragraph({ text: '' }),
          new Paragraph({ text: `User: ${summaryData.user_email}` }),
          new Paragraph({ text: `Period: ${summaryData.start_date} to ${summaryData.end_date}` }),
          new Paragraph({ text: `Days with data: ${summaryData.num_days_with_data}` }),
          new Paragraph({ text: '' }),
          new Paragraph({ text: 'Meals', heading: HeadingLevel.HEADING_2 }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: tableRows
          }),
          new Paragraph({ text: '' }),
          new Paragraph({ text: 'Daily Averages', heading: HeadingLevel.HEADING_2 }),
          new Paragraph({ text: '' }),
          new Paragraph({ text: 'Macronutrients', heading: HeadingLevel.HEADING_3 }),
          new Paragraph({ text: `Calories: ${avg.calories.toFixed(0)} kcal | Protein: ${avg.protein_g.toFixed(1)}g | Carbs: ${avg.carbs_g.toFixed(1)}g | Fat: ${avg.fat_g.toFixed(1)}g` }),
          new Paragraph({ text: '' }),
          new Paragraph({ text: 'Vitamins', heading: HeadingLevel.HEADING_3 }),
          new Paragraph({ text: `Vitamin A: ${(avg.vitamin_a_mcg || 0).toFixed(1)} mcg | Vitamin C: ${(avg.vitamin_c_mg || 0).toFixed(1)} mg | Folate: ${(avg.folate_mcg || 0).toFixed(1)} mcg | B12: ${(avg.vitamin_b12_mcg || 0).toFixed(1)} mcg` }),
          new Paragraph({ text: '' }),
          new Paragraph({ text: 'Minerals', heading: HeadingLevel.HEADING_3 }),
          new Paragraph({ text: `Calcium: ${(avg.calcium_mg || 0).toFixed(1)} mg | Iron: ${(avg.iron_mg || 0).toFixed(1)} mg | Iodine: ${(avg.iodine_mcg || 0).toFixed(1)} mcg` }),
          new Paragraph({ text: `Magnesium: ${(avg.magnesium_mg || 0).toFixed(1)} mg | Potassium: ${(avg.potassium_mg || 0).toFixed(1)} mg | Sodium: ${(avg.sodium_mg || 0).toFixed(1)} mg` })
        ]
      }]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `meal-summary-${summaryData.user_email}-${startDate}.docx`);
    toast.success('Word document downloaded successfully');
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading meal summary...</div>;
  }

  if (!summaryData || !summaryData.meals) {
    return <div className="text-center py-8 text-muted-foreground">No meal data available for this period</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with date picker and export buttons */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card rounded-xl p-4 border border-border/50">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm font-medium">Week starting:</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 bg-muted border border-border rounded-lg text-sm"
            data-testid="meal-summary-date"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPDF}
            className="flex items-center gap-2"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" />
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadWord}
            className="flex items-center gap-2"
            data-testid="download-word-btn"
          >
            <FileDown className="w-4 h-4" />
            Word
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border border-border/50 p-4 text-center">
          <p className="text-2xl font-bold text-primary">{summaryData.meals.length}</p>
          <p className="text-xs text-muted-foreground">Total Meals</p>
        </div>
        <div className="bg-card rounded-xl border border-border/50 p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{summaryData.num_days_with_data || 0}</p>
          <p className="text-xs text-muted-foreground">Days Tracked</p>
        </div>
        <div className="bg-card rounded-xl border border-border/50 p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{summaryData.daily_averages?.calories?.toFixed(0) || 0}</p>
          <p className="text-xs text-muted-foreground">Avg Calories/Day</p>
        </div>
        <div className="bg-card rounded-xl border border-border/50 p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{summaryData.daily_averages?.protein_g?.toFixed(0) || 0}g</p>
          <p className="text-xs text-muted-foreground">Avg Protein/Day</p>
        </div>
      </div>

      {/* Meals table */}
      <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
        <div className="p-4 border-b border-border bg-muted/50">
          <h4 className="font-bold">Meals ({summaryData.start_date} to {summaryData.end_date})</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase">Description</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Calories</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Protein</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Carbs</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase">Fat</th>
              </tr>
            </thead>
            <tbody>
              {summaryData.meals.map((meal, idx) => (
                <tr key={`meal-${meal.id || idx}`} className="border-b border-border/50 hover:bg-muted/20">
                  <td className="px-4 py-3 text-sm">{formatDate(meal.date)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      meal.meal_type === 'breakfast' ? 'bg-yellow-100 text-yellow-700' :
                      meal.meal_type === 'lunch' ? 'bg-green-100 text-green-700' :
                      meal.meal_type === 'dinner' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {getMealTypeLabel(meal.meal_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm max-w-xs truncate">{meal.description || 'No description'}</td>
                  <td className="px-4 py-3 text-sm text-right">{meal.calories?.toFixed(0) || 0}</td>
                  <td className="px-4 py-3 text-sm text-right">{meal.protein_g?.toFixed(1) || 0}g</td>
                  <td className="px-4 py-3 text-sm text-right">{meal.carbs_g?.toFixed(1) || 0}g</td>
                  <td className="px-4 py-3 text-sm text-right">{meal.fat_g?.toFixed(1) || 0}g</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Averages breakdown */}
      {summaryData.daily_averages && (
        <>
          <div className="bg-card rounded-2xl border border-border/50 p-6">
            <h4 className="font-bold mb-4">Daily Averages - Macronutrients</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/50 rounded-xl p-4 text-center">
                <p className="text-xl font-bold">{summaryData.daily_averages.calories?.toFixed(0) || 0}</p>
                <p className="text-xs text-muted-foreground">Calories</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-4 text-center">
                <p className="text-xl font-bold">{summaryData.daily_averages.protein_g?.toFixed(1) || 0}g</p>
                <p className="text-xs text-muted-foreground">Protein</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-4 text-center">
                <p className="text-xl font-bold">{summaryData.daily_averages.carbs_g?.toFixed(1) || 0}g</p>
                <p className="text-xs text-muted-foreground">Carbohydrates</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-4 text-center">
                <p className="text-xl font-bold">{summaryData.daily_averages.fat_g?.toFixed(1) || 0}g</p>
                <p className="text-xs text-muted-foreground">Fat</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border/50 p-6">
            <h4 className="font-bold mb-4">Daily Averages - Micronutrients</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold">{(summaryData.daily_averages.vitamin_a_mcg || 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">Vitamin A (mcg)</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold">{(summaryData.daily_averages.vitamin_c_mg || 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">Vitamin C (mg)</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold">{(summaryData.daily_averages.calcium_mg || 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">Calcium (mg)</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold">{(summaryData.daily_averages.iron_mg || 0).toFixed(1)}</p>
                <p className="text-xs text-muted-foreground">Iron (mg)</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold">{(summaryData.daily_averages.potassium_mg || 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">Potassium (mg)</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-lg font-bold">{(summaryData.daily_averages.sodium_mg || 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">Sodium (mg)</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MealLogSummary;
